import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../../services/api';
import type { ConversationSummary } from '../../types/admin';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SentimentBadge from '../../components/admin/SentimentBadge';
import SearchBar from '../../components/admin/SearchBar';
import FilterPanel, { type FilterState } from '../../components/admin/FilterPanel';
import { ChatBubbleLeftRightIcon, ChevronLeftIcon, ChevronRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

// Format date/time in user's local timezone
const formatLocalDateTime = (dateString: string): string => {
  // Ensure we're parsing the date as UTC if it doesn't have timezone info
  const date = dateString.endsWith('Z') ? new Date(dateString) : new Date(dateString + 'Z');
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export default function ConversationsPage() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<FilterState>({ activeOnly: false, sentimentFilter: 'All' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLimited, setIsLimited] = useState(false);
  const [matchedKeywords, setMatchedKeywords] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 20;

  useEffect(() => {
    loadConversations();
  }, [searchQuery, filters, currentPage]);

  const loadConversations = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('search', searchQuery);
      if (filters.userId) params.append('userId', filters.userId);
      if (filters.startDate) params.append('startDate', filters.startDate);
      if (filters.endDate) params.append('endDate', filters.endDate);
      if (filters.activeOnly) params.append('activeOnly', 'true');
      if (filters.sentimentFilter) params.append('sentimentFilter', filters.sentimentFilter);
      params.append('page', currentPage.toString());
      params.append('pageSize', pageSize.toString());

      const response = await adminApi.getConversations(params.toString());
      setConversations(response.conversations);
      setTotalPages(response.totalPages || 1);
      setTotalCount(response.totalCount || 0);
      setIsLimited(response.isLimited || false);
      setMatchedKeywords(response.matchedKeywords || []);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load conversations';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page on search
  }, []);

  const handleFilterChange = (newFilters: FilterState) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page on filter change
  };

  const handleRowClick = (sessionId: string) => {
    navigate(`/admin/conversations/${sessionId}`, {
      state: { matchedKeywords }
    });
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (isLoading && conversations.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <div className="p-6" role="main" aria-label="Conversations management">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Conversations</h1>
        <p className="mt-1 text-sm text-gray-600">
          View and monitor user chat sessions with sentiment analysis
        </p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 rounded-md bg-red-50 p-4 border border-red-200" role="alert">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading conversations</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadConversations}
                className="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <SearchBar onSearch={handleSearch} />
        </div>
        <FilterPanel onFilterChange={handleFilterChange} />
      </div>

      {/* 500 Result Warning */}
      {isLimited && (
        <div className="mb-4 rounded-md bg-yellow-50 p-4 border border-yellow-200">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Results limited to 500 conversations
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Your search returned more than 500 results. Please refine your filters to see all matches.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Matched Keywords Display */}
      {matchedKeywords.length > 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          <span className="font-medium">Searching for:</span>
          {matchedKeywords.map((keyword, index) => (
            <span key={index} className="inline-flex items-center rounded-full bg-indigo-100 px-2.5 py-0.5 text-xs font-medium text-indigo-800">
              {keyword}
            </span>
          ))}
        </div>
      )}

      {/* Active Filter Badges */}
      {(filters.sentimentFilter !== 'All' || filters.activeOnly) && (
        <div className="mb-4 flex items-center gap-2 text-sm">
          <span className="font-medium text-gray-600">Active filters:</span>
          {filters.sentimentFilter === 'NeedsAttention' && (
            <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800">
              ⚠️ Needs Attention
            </span>
          )}
          {filters.activeOnly && (
            <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
              Active Sessions Only
            </span>
          )}
        </div>
      )}

      {conversations.length === 0 ? (
        <div className="text-center py-12">
          <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            {searchQuery || filters.userId || filters.startDate || filters.endDate || filters.activeOnly
              ? 'No conversations found'
              : 'No conversations'}
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchQuery || filters.userId || filters.startDate || filters.endDate || filters.activeOnly
              ? 'Try adjusting your search or filters.'
              : 'User conversations will appear here once they start chatting.'}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-300" role="table" aria-label="Conversations list">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900">
                    User
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Last Message
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Messages
                  </th>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                    Sentiment
                  </th>
                  <th className="relative py-3.5 pl-3 pr-4">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {conversations.map((conversation) => (
                  <tr
                    key={conversation.sessionId}
                    onClick={() => handleRowClick(conversation.sessionId)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm">
                      <div className="flex items-center">
                        <div className="font-medium text-gray-900">
                          {conversation.userName}
                        </div>
                        {conversation.hasNegativeSentiment && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800">
                            ⚠️ Needs Attention
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatLocalDateTime(conversation.lastMessageAt)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {conversation.messageCount}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <SentimentBadge
                        classification={conversation.sentimentClassification}
                        score={conversation.sentimentScore}
                        size="sm"
                      />
                    </td>
                    <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium">
                      <span className="text-indigo-600 hover:text-indigo-900">
                        View →
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6 rounded-lg">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing page <span className="font-medium">{currentPage}</span> of{' '}
                    <span className="font-medium">{totalPages}</span> ({totalCount} total conversations)
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center rounded-r-md px-2 py-2 text-gray-400 ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-20 focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
